import { useResetScrollToTop } from '@zuto/utilities/src/Hooks/useResetScrollToTop'
import React, {
  Context, useContext, useEffect, useReducer
} from 'react'
import { useQuestionVisibilityTracking } from '../../../hooks/useQuestionVisibilityTracking'
import QuestionComponentProps, { UpdateFollowingPrefillStepType } from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import { getStartingIndex } from './history-utils'
import { HistoryContextProps, HistoryState } from './HistoryContext'
import HistoryEntry from './HistoryEntry'
import { HistoryEntryProps } from './HistoryEntryProps'
import { createHistoryReducerWithMinimumYears } from './HistoryReducer'
import { useEscapeAction } from './useEscapeAction'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'

type CreateHistoryQuestionProps<EntryType extends HistoryEntry> = QuestionComponentProps & {
  questionKey: QuestionKey
  HistoryContext: Context<HistoryContextProps<EntryType>>
  EntryComponent: (props: HistoryEntryProps<EntryType>) => JSX.Element
  minimumYearsHistoryRequired : number
  entryValidator: (entry: EntryType) => boolean
  updateFollowingPrefillStep?: UpdateFollowingPrefillStepType
}

export const HistoryQuestion = <EntryType extends HistoryEntry>({
  questionKey,
  HistoryContext,
  EntryComponent,
  minimumYearsHistoryRequired,
  entryValidator,
  existingAnswers,
  returningViaPrevious,
  disablePrevious,
  updateFollowingPrefillStep,
  eventsServiceUrl,
  appFormEventParams
}: CreateHistoryQuestionProps<EntryType>): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const startingEntries = existingAnswers?.entries ?? {}
  const { featureSwitches } = appFormContext
  const { reviewPageV3 } = featureSwitches ?? {}
  const { requiredEntryId } = useContext(PrefillReviewPageApplicationFormContext)

  useQuestionVisibilityTracking(questionKey, { eventsServiceUrl, appFormEventParams })
  useResetScrollToTop()

  const initialState : HistoryState<EntryType> = {
    entries: startingEntries,
    currentIndex: getStartingIndex(
      startingEntries, entryValidator, minimumYearsHistoryRequired, !!returningViaPrevious, !!reviewPageV3, requiredEntryId
    ),
    startAtEndOfEntry: !!returningViaPrevious
  }

  const reducer = createHistoryReducerWithMinimumYears<EntryType>(minimumYearsHistoryRequired)
  const [state, dispatch] = useReducer(reducer, initialState)

  useEscapeAction<EntryType>(appFormContextDispatch, state, questionKey)

  useEffect(() => {
    if (reviewPageV3) {
      /*
        We usually only dispatch to appFormContext once we have enough history, but since we are now allowing them to
        edit individual entries, we need to dispatch when the entry changes.
      */
      if (questionKey === 'employmentHistory') {
        appFormContextDispatch({
          type: 'QUESTION_ANSWERED',
          name: QuestionKey.EMPLOYMENT_HISTORY,
          value: state
        })
      }
      if (questionKey === 'addressHistory') {
        appFormContextDispatch({
          type: 'QUESTION_ANSWERED',
          name: QuestionKey.ADDRESS_HISTORY,
          value: state
        })
      }
    }
  }, [state.entries])

  return (
    <HistoryContext.Provider value={{ dispatch }}>
      <EntryComponent
        key={state.currentIndex}
        entryId={state.currentIndex}
        existingEntry={state.entries[state.currentIndex]}
        disablePrevious={disablePrevious && state.currentIndex === '1'}
        startAtEnd={state.startAtEndOfEntry}
        updateFollowingPrefillStep={updateFollowingPrefillStep}
      />
    </HistoryContext.Provider>
  )
}
